import React, { useEffect } from 'react';
import styled from 'styled-components';
import { X, Calendar } from 'lucide-react';

interface Disease {
  id: number;
  name: string;
}

interface User {
  id: number;
  fullName: string;
}

interface FilterValues {
  caseManagerFilter: string;
  diseaseFilter: string;
  dateFrom: string;
  dateTo: string;
}

interface ClientFilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: FilterValues) => void;
  onClear: () => void;
  filters: FilterValues;
  diseases: Disease[];
  caseManagers: User[];
  caseManagerLoads: Record<number, number>;
  showCaseManagerFilter: boolean;
}

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: all 0.2s ease;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalContainer = styled.div<{ $isOpen: boolean }>`
  width: 420px;
  max-width: 90vw;
  max-height: 85vh;
  background: var(--surface-color);
  border-radius: var(--border-radius-lg);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  transform: scale(${props => props.$isOpen ? '1' : '0.95'});
  opacity: ${props => props.$isOpen ? 1 : 0};
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const DrawerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid var(--border-color);
`;

const DrawerTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: var(--border-radius-md);
  background: transparent;
  color: var(--text-tertiary);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--hover-background);
    color: var(--text-primary);
  }
`;

const DrawerBody = styled.div`
  flex: 1;
  padding: 24px;
  overflow-y: auto;
`;

const FilterGroup = styled.div`
  margin-bottom: 24px;
`;

const FilterLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  font-size: 14px;
  background: var(--surface-color);
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px var(--primary-light);
  }
`;

const DateRangeContainer = styled.div`
  display: flex;
  gap: 12px;
`;

const DateInputWrapper = styled.div`
  flex: 1;
`;

const DateInputLabel = styled.span`
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  color: var(--text-tertiary);
`;

const DateInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  font-size: 14px;
  background: var(--surface-color);
  transition: all 0.2s ease;
  color: var(--text-primary);

  &::-webkit-datetime-edit-text,
  &::-webkit-datetime-edit-month-field,
  &::-webkit-datetime-edit-day-field,
  &::-webkit-datetime-edit-year-field {
    color: ${props => props.value ? 'var(--text-primary)' : 'transparent'};
  }

  &:focus::-webkit-datetime-edit-text,
  &:focus::-webkit-datetime-edit-month-field,
  &:focus::-webkit-datetime-edit-day-field,
  &:focus::-webkit-datetime-edit-year-field {
    color: var(--text-primary);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px var(--primary-light);
  }
`;

const DrawerFooter = styled.div`
  display: flex;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid var(--border-color);
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  flex: 1;
  padding: 12px 16px;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.variant === 'primary' ? `
    background: var(--primary-color);
    color: white;
    border: 1px solid var(--primary-color);
    &:hover { background: transparent; color: var(--primary-color); }
  ` : `
    background: var(--hover-background);
    color: var(--text-primary);
    &:hover { background: var(--border-color); }
  `}
`;

const ClientFilterDrawer: React.FC<ClientFilterDrawerProps> = ({
  isOpen,
  onClose,
  onApply,
  onClear,
  filters,
  diseases,
  caseManagers,
  caseManagerLoads,
  showCaseManagerFilter
}) => {
  const [localFilters, setLocalFilters] = React.useState<FilterValues>(filters);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleChange = (field: keyof FilterValues, value: string) => {
    setLocalFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleApply = () => {
    onApply(localFilters);
    onClose();
  };

  const handleClear = () => {
    const clearedFilters: FilterValues = {
      caseManagerFilter: '',
      diseaseFilter: '',
      dateFrom: '',
      dateTo: ''
    };
    setLocalFilters(clearedFilters);
    onClear();
  };

  return (
    <Overlay $isOpen={isOpen} onClick={onClose}>
      <ModalContainer $isOpen={isOpen} onClick={(e) => e.stopPropagation()}>
        <DrawerHeader>
          <DrawerTitle>מסננים מתקדמים</DrawerTitle>
          <CloseButton onClick={onClose} title="סגור">
            <X size={20} />
          </CloseButton>
        </DrawerHeader>

        <DrawerBody>
          {showCaseManagerFilter && (
            <FilterGroup>
              <FilterLabel>מנהל תיק</FilterLabel>
              <Select
                value={localFilters.caseManagerFilter}
                onChange={(e) => handleChange('caseManagerFilter', e.target.value)}
              >
                <option value="">כל מנהלי התיקים</option>
                <option value="-1">לא מוקצה</option>
                {caseManagers.map(manager => (
                  <option key={manager.id} value={manager.id}>
                    {manager.fullName}
                    {caseManagerLoads[manager.id] !== undefined && ` (${caseManagerLoads[manager.id]} תיקים)`}
                  </option>
                ))}
              </Select>
            </FilterGroup>
          )}

          <FilterGroup>
            <FilterLabel>מחלה</FilterLabel>
            <Select
              value={localFilters.diseaseFilter}
              onChange={(e) => handleChange('diseaseFilter', e.target.value)}
            >
              <option value="">כל המחלות</option>
              {diseases.map(disease => (
                <option key={disease.id} value={disease.id}>
                  {disease.name}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>
              <Calendar size={14} style={{ marginLeft: '6px', verticalAlign: 'middle' }} />
              טווח תאריכים (חתימת חוזה)
            </FilterLabel>
            <DateRangeContainer>
              <DateInputWrapper>
                <DateInputLabel>מתאריך</DateInputLabel>
                <DateInput
                  type="date"
                  value={localFilters.dateFrom}
                  onChange={(e) => handleChange('dateFrom', e.target.value)}
                />
              </DateInputWrapper>
              <DateInputWrapper>
                <DateInputLabel>עד תאריך</DateInputLabel>
                <DateInput
                  type="date"
                  value={localFilters.dateTo}
                  onChange={(e) => handleChange('dateTo', e.target.value)}
                />
              </DateInputWrapper>
            </DateRangeContainer>
          </FilterGroup>
        </DrawerBody>

        <DrawerFooter>
          <Button variant="primary" onClick={handleApply}>
            החל מסננים
          </Button>
          <Button onClick={handleClear}>
            נקה הכל
          </Button>
        </DrawerFooter>
      </ModalContainer>
    </Overlay>
  );
};

export default ClientFilterDrawer;
